/**
 * Provides helper functions for managing theme styles.
 */

import { StyleSheet, ShadowPropTypesIOSStatic, RegisteredStyle, ViewStyle } from 'react-native';

import { Colors } from 'theme/colors';

const MAX_IN_ELEVATION = 24;
const MAX_IN_BLUR = 38;
const MIN_OUT_ELEVATION = 0.2;
const MAX_OUT_ELEVATION = 0.6;
const MAX_OUT_BLUR = 16;

type Shadow = Pick<ViewStyle, 'backgroundColor' | 'elevation'> & ShadowPropTypesIOSStatic;
interface ShadowSheet {
  shadow: RegisteredStyle<Shadow> | Shadow;
}
const _shadows = new Map<number, ShadowSheet>();

/**
 * Retrieves the appropriate shadow style for a given elevation.
 *
 * @param elevation - Depth level as defined by Material Design principles.
 * @returns Shadow style.
 */
export function getShadow(elevation: number) {
  let sheet = _shadows.get(elevation);

  if (sheet) {
    return sheet.shadow;
  }

  sheet = StyleSheet.create({
    shadow: parseElevation(elevation),
  });
  _shadows.set(elevation, sheet);

  return sheet.shadow;
}
/**
 * Below is the configuration for Material Design used by Google.
 *
 * @see https://github.com/material-components/material-components-web/blob/master/packages/mdc-elevation/_variables.scss
 */
const androidDepth = {
  ambient: [
    '0px 0px 0px 0px',
    '0px 1px 3px 0px',
    '0px 1px 5px 0px',
    '0px 1px 8px 0px',
    '0px 1px 10px 0px',
    '0px 1px 14px 0px',
    '0px 1px 18px 0px',
    '0px 2px 16px 1px',
    '0px 3px 14px 2px',
    '0px 3px 16px 2px',
    '0px 4px 18px 3px',
    '0px 4px 20px 3px',
    '0px 5px 22px 4px',
    '0px 5px 24px 4px',
    '0px 5px 26px 4px',
    '0px 6px 28px 5px',
    '0px 6px 30px 5px',
    '0px 6px 32px 5px',
    '0px 7px 34px 6px',
    '0px 7px 36px 6px',
    '0px 8px 38px 7px',
    '0px 8px 40px 7px',
    '0px 8px 42px 7px',
    '0px 9px 44px 8px',
    '0px 9px 46px 8px',
  ],
  penumbra: [
    '0px 1px 1px 0px',
    '0px 2px 2px 0px',
    '0px 3px 4px 0px',
    '0px 4px 5px 0px',
    '0px 5px 8px 0px',
    '0px 6px 10px 0px',
    '0px 7px 10px 1px',
    '0px 8px 10px 1px',
    '0px 9px 12px 1px',
    '0px 10px 14px 1px',
    '0px 11px 15px 1px',
    '0px 12px 17px 2px',
    '0px 13px 19px 2px',
    '0px 14px 21px 2px',
    '0px 15px 22px 2px',
    '0px 16px 24px 2px',
    '0px 17px 26px 2px',
    '0px 18px 28px 2px',
    '0px 19px 29px 2px',
    '0px 20px 31px 3px',
    '0px 21px 33px 3px',
    '0px 22px 35px 3px',
    '0px 23px 36px 3px',
    '0px 24px 38px 3px',
  ],
  umbra: [
    '0px 2px 1px -1px',
    '0px 3px 1px -2px',
    '0px 3px 3px -2px',
    '0px 2px 4px -1px',
    '0px 3px 5px -1px',
    '0px 3px 5px -1px',
    '0px 4px 5px -2px',
    '0px 5px 5px -3px',
    '0px 5px 6px -3px',
    '0px 6px 6px -3px',
    '0px 6px 7px -4px',
    '0px 7px 8px -4px',
    '0px 7px 8px -4px',
    '0px 7px 9px -4px',
    '0px 8px 9px -5px',
    '0px 8px 10px -5px',
    '0px 8px 11px -5px',
    '0px 9px 11px -5px',
    '0px 9px 12px -6px',
    '0px 10px 13px -6px',
    '0px 10px 13px -6px',
    '0px 10px 14px -6px',
    '0px 11px 14px -7px',
    '0px 11px 15px -7px',
  ],
};

/**
 * Parses a box shadow CSS value to its constituent components.
 *
 *
 * @param css - Box shadow in CSS.
 * @returns An object describing the properties of the shadow.
 */
function parseShadow(css: string) {
  const values = css.split(' ').map(val => Number(val.replace('px', '')));

  return {
    blur: values[2],
    x: values[0],
    y: values[1],
  };
}
/**
 * Interpolates a value from a given input tuple, to an output tuple.
 *
 * @param value - Value to interpolate.
 * @param input - Input range of numbers.
 * @param output - Output range of numbers.
 * @returns A value corresponding to the interpolated value.
 */
function interpolate(value: number, input: [number, number], output: [number, number]) {
  const [minInput, maxInput] = input;
  const [minOutput, maxOutput] = output;

  return ((value - minInput) * (maxOutput - minOutput)) / (maxInput - minInput) + minOutput;
}
/**
 * Converts an elevation to a React Native style definition.
 *
 * @param elevation - Elevation to convert.
 * @returns React Native shadow equivalent.
 */
function parseElevation(elevation: number): Shadow {
  const shadow = parseShadow(androidDepth.penumbra[elevation]);

  return {
    backgroundColor: Colors.white,
    elevation,
    shadowColor: Colors.black,
    shadowOffset: {
      height: shadow.y === 1 ? 1 : Math.floor(shadow.y / 2),
      width: 0,
    },
    shadowOpacity: interpolate(
      elevation,
      [1, MAX_IN_ELEVATION],
      [MIN_OUT_ELEVATION, MAX_OUT_ELEVATION]
    ),
    shadowRadius: interpolate(shadow.blur, [1, MAX_IN_BLUR], [1, MAX_OUT_BLUR]),
  };
}
